import { ref, computed, readonly } from "vue";
import {
  healthReportApi,
  type HealthReportDetail,
  type HealthReportSummary,
  type TestHistoryPoint,
  type LabResultView,
  type CategoryRecord as ApiCategoryRecord,
} from "@/api/healthReport.api";
import {
  type CategoryRecord as MockCategoryRecord,
  type CategoryTest as MockCategoryTest,
  type CategoryType as MockCategoryType,
  type ComparisonRow as MockComparisonRow,
  type ComparisonTrend,
  type FullSummaryRecord as MockFullSummary,
  type RangeSegment,
  type SummaryCategory as MockSummaryCategory,
  type TestDetailRecord as MockTestDetail,
  type TestStatus,
  type VitalSign,
} from "./healthReport.types";

/**
 * Phase 7.2 / 7.2.1 — real customer-side health-report composable.
 *
 * Strategy: expose the SAME getter shape that the existing 4 health
 * pages already consume from `useHealthReportMock` (getCategory /
 * getTest / getFullSummary). When the BE has a published report for
 * the caller, adapt it into the mock shape on the fly. When the BE
 * returns nothing (fresh customer, or call failed), fall back to the
 * mock so screens still render — this keeps the demo experience
 * intact without forcing customers through registration before
 * "ดูตัวอย่าง".
 *
 * The adapter computes `RangeSegment[]` from the 4 numeric bounds in
 * `LabResultView` (refMin / refMax / warnMin / warnMax). Each present
 * pair adds one coloured segment. Missing bounds shrink the bar.
 */

// Module-scoped — multiple page components share the same fetch.
const list = ref<HealthReportSummary[]>([]);
const latest = ref<HealthReportDetail | null>(null);
/** Phase 7.2.4 — second-newest published report, for year-over-year diff
 *  on the FullSummary page. Null when the customer has only one report. */
const previous = ref<HealthReportDetail | null>(null);
const listLoading = ref(false);
const latestLoading = ref(false);
const error = ref<string | null>(null);

async function refreshList(): Promise<void> {
  listLoading.value = true;
  error.value = null;
  try {
    list.value = await healthReportApi.list();
  } catch (e) {
    error.value = (e as Error).message;
    list.value = [];
  } finally {
    listLoading.value = false;
  }
}

async function loadLatest(): Promise<void> {
  if (list.value.length === 0) await refreshList();
  const first = list.value[0];
  if (!first) {
    latest.value = null;
    return;
  }
  latestLoading.value = true;
  try {
    latest.value = await healthReportApi.get(first.id);
  } catch (e) {
    error.value = (e as Error).message;
    latest.value = null;
  } finally {
    latestLoading.value = false;
  }
}

async function loadReport(id: string): Promise<HealthReportDetail | null> {
  latestLoading.value = true;
  try {
    const r = await healthReportApi.get(id);
    latest.value = r;
    return r;
  } catch (e) {
    error.value = (e as Error).message;
    return null;
  } finally {
    latestLoading.value = false;
  }
}

async function loadHistory(
  testCode: string,
  months?: number,
): Promise<TestHistoryPoint[]> {
  try {
    return await healthReportApi.history(testCode, months);
  } catch (e) {
    error.value = (e as Error).message;
    return [];
  }
}

/**
 * Phase 7.2.4 — load latest + previous reports in parallel so the
 * FullSummary page can render year-over-year comparisons.
 */
async function loadFullSummary(): Promise<void> {
  if (list.value.length === 0) await refreshList();
  const items = list.value;
  if (items.length === 0) {
    latest.value = null;
    previous.value = null;
    return;
  }
  latestLoading.value = true;
  try {
    const [a, b] = await Promise.all([
      healthReportApi.get(items[0].id),
      items[1] ? healthReportApi.get(items[1].id) : Promise.resolve(null),
    ]);
    latest.value = a;
    previous.value = b;
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    latestLoading.value = false;
  }
}

// ============================================================
// Shape adapters — API CategoryRecord → mock CategoryRecord etc.
// ============================================================

const COLORS = {
  bad: "#D83A5A",
  warn: "#E89740",
  warnLow: "#E8C547",
  ok: "#5BC78A",
  neutral: "#94a3b8",
} as const;

const THAI_MONTHS = [
  "ม.ค.",
  "ก.พ.",
  "มี.ค.",
  "เม.ย.",
  "พ.ค.",
  "มิ.ย.",
  "ก.ค.",
  "ส.ค.",
  "ก.ย.",
  "ต.ค.",
  "พ.ย.",
  "ธ.ค.",
];

const CATEGORY_INFO: Record<string, { name: string; subtitle: string }> = {
  heart: { name: "หัวใจ", subtitle: "Cardiovascular" },
  sugar: { name: "น้ำตาลในเลือด", subtitle: "Blood Sugar" },
  lipid: { name: "ไขมัน", subtitle: "Lipid Profile" },
  liver: { name: "ตับ", subtitle: "Liver Function" },
  kidney: { name: "ไต", subtitle: "Kidney Function" },
  lung: { name: "ปอด", subtitle: "Pulmonary" },
  general: { name: "ทั่วไป", subtitle: "General Health" },
};

function formatThaiDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${THAI_MONTHS[d.getMonth()]} ${d.getFullYear() + 543}`;
}

/** Hospital display label; marks reports issued by another hospital. */
export function hospitalLabel(r: {
  hospId: number | null;
  hospName: string | null;
  isOtherHospital: boolean;
}): string {
  const name = r.hospName ?? (r.hospId !== null ? `โรงพยาบาล #${r.hospId}` : "—");
  return r.isOtherHospital ? `${name} (ต่างโรงพยาบาล)` : name;
}

function formatThaiDateTime(iso: string): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString("th-TH", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${formatThaiDate(iso)} เวลา ${time} น.`;
}

/**
 * Build the colour-coded segment array for HealthRangeBar from the
 * 4 numeric bounds on a LabResultView. Missing bounds shrink the bar
 * (e.g. HDL has only refMin/warnMin → 2 bands: "below danger" + "ok").
 */
function computeRangeSegments(t: LabResultView): RangeSegment[] {
  const { refMin, refMax, warnMin, warnMax } = t;
  const segments: RangeSegment[] = [];
  let prev = 0;

  // Scale-aware overshoot: proportional to the actual normal span (avoids crushing small-number tests like Creatinine)
  const effectiveMax = warnMax ?? refMax ?? 100;
  const effectiveMin = warnMin ?? 0;
  const span = Math.max(effectiveMax - effectiveMin, 0.1);
  const overshoot = Math.max(span * 0.25, 0.1);

  if (warnMin !== null) {
    segments.push({
      range: warnMin,
      color: COLORS.bad,
      axisLabel: "0",
      status: "bad",
      legendLabel: "ต่ำอันตราย",
      legendRange: `< ${warnMin}`,
    });
    prev = warnMin;
  }
  if (refMin !== null && refMin > prev) {
    segments.push({
      range: refMin - prev,
      color: COLORS.warnLow,
      axisLabel: String(prev),
      status: "warn",
      legendLabel: "ต่ำกว่าเกณฑ์",
      legendRange: prev === 0 ? `< ${refMin}` : `${prev} - ${refMin}`,
    });
    prev = refMin;
  }
  if (refMax !== null && refMax > prev) {
    segments.push({
      range: refMax - prev,
      color: COLORS.ok,
      axisLabel: String(prev),
      status: "ok",
      legendLabel: "ปกติ",
      legendRange: `${prev} - ${refMax}`,
    });
    prev = refMax;
  }
  if (warnMax !== null && warnMax > prev) {
    segments.push({
      range: warnMax - prev,
      color: COLORS.warn,
      axisLabel: String(prev),
      status: "warn",
      legendLabel: "เฝ้าระวัง / เริ่มสูง",
      legendRange: `${prev} - ${warnMax}`,
    });
    prev = warnMax;
  }
  if (warnMax !== null) {
    // Visual overshoot for high danger zone
    segments.push({
      range: overshoot,
      color: COLORS.bad,
      axisLabel: String(warnMax),
      status: "bad",
      legendLabel: "สูงกว่าเกณฑ์",
      legendRange: `> ${warnMax}`,
    });
  } else if (refMin !== null && refMax === null && warnMax === null) {
    // "higher is better" tests (e.g. HDL, eGFR)
    segments.push({
      range: overshoot,
      color: COLORS.ok,
      axisLabel: String(prev),
      status: "ok",
      legendLabel: "ปกติ",
      legendRange: `≥ ${refMin}`,
    });
  } else if (refMax !== null && warnMax === null) {
    // Simple 2-band test (<= refMax is ok, > refMax is bad)
    segments.push({
      range: overshoot,
      color: COLORS.bad,
      axisLabel: String(refMax),
      status: "bad",
      legendLabel: "สูงกว่าเกณฑ์",
      legendRange: `> ${refMax}`,
    });
  }

  if (segments.length === 0) {
    return [{ range: 100, color: COLORS.neutral, axisLabel: "", status: "ok", legendLabel: "ปกติ" }];
  }
  return segments;
}

function labResultToMockTest(t: LabResultView): MockCategoryTest {
  return {
    id: t.testCode.toLowerCase(),
    name: t.name,
    description: t.description ?? "",
    value: t.valueNumeric ?? 0,
    unit: t.unit ?? "",
    status: (t.status ?? "ok") as TestStatus,
    rangeSegments: computeRangeSegments(t),
  };
}

function apiToMockCategoryRecord(
  report: HealthReportDetail,
  apiCat: ApiCategoryRecord,
): MockCategoryRecord {
  const info = CATEGORY_INFO[apiCat.category] ?? {
    name: apiCat.category,
    subtitle: "",
  };
  return {
    category: {
      type: apiCat.category as MockCategoryType,
      name: info.name,
      subtitle: info.subtitle,
      testDate: formatThaiDate(report.reportDate),
    },
    doctorAdvice: report.doctorAdvice ?? undefined,
    tests: apiCat.tests.map(labResultToMockTest),
    labInfo: {
      labName: hospitalLabel(report),
      collectedAt: formatThaiDateTime(report.reportDate),
    },
  };
}

function findCategoryInLatest(type: string): ApiCategoryRecord | null {
  if (!latest.value) return null;
  return latest.value.categories.find((c) => c.category === type) ?? null;
}

function apiToMockTestDetail(
  report: HealthReportDetail,
  test: LabResultView,
  category: ApiCategoryRecord,
  history: TestHistoryPoint[],
): MockTestDetail {
  const info = CATEGORY_INFO[category.category] ?? {
    name: category.category,
    subtitle: "",
  };
  // Threshold = first bad-side bound, used by the FE for the "เกิน X" line.
  const threshold =
    test.warnMax !== null
      ? test.warnMax
      : test.refMax !== null
        ? test.refMax
        : test.refMin !== null
          ? test.refMin
          : 0;
  return {
    testName: test.plainName && test.plainName !== test.name ? `${test.plainName} (${test.name})` : test.name,
    testSubtitle: test.description ?? undefined,
    categoryType: category.category as MockCategoryType,
    value: test.valueNumeric ?? 0,
    unit: test.unit ?? "",
    status: (test.status ?? "ok") as TestStatus,
    threshold,
    rangeSegments: computeRangeSegments(test),
    history: history.slice(0, 6).map((h) => {
      const d = new Date(h.reportDate);
      return {
        value: h.valueNumeric ?? 0,
        status: (h.status ?? "ok") as TestStatus,
        label: `${d.getDate()}/${d.getMonth() + 1}`,
      };
    }),
    labDetails: [
      { label: "โรงพยาบาล", value: hospitalLabel(report) },
      { label: "วันที่ตรวจ", value: formatThaiDate(report.reportDate) },
      { label: "ประเภทตรวจ", value: report.reportType },
      {
        label: "สถานะ",
        value:
          report.overallStatus === "ok"
            ? "ปกติ"
            : report.overallStatus === "watch"
              ? "เฝ้าระวัง"
              : report.overallStatus === "abnormal"
                ? "ผิดปกติ"
                : "—",
        success: report.overallStatus === "ok",
      },
    ],
    explainer: {
      title: test.explainerTitle || `เกี่ยวกับ${test.name}`,
      text:
        test.explainerText ||
        test.description ||
        `ค่า${test.name}เป็นตัวบ่งชี้สถานะ${info.name} — แพทย์จะใช้ค่านี้ประเมินสุขภาพร่วมกับตัวอื่น`,
    },
    recommendations:
      Array.isArray(test.recommendations) && test.recommendations.length > 0
        ? test.recommendations
        : deriveRecommendations(test),
    certifiedBy: report.certifiedBy.usrid
      ? {
          doctorName: report.certifiedBy.usrid,
          licenseNo: report.certifiedBy.hospId ?? "",
        }
      : undefined,
  };
}

// ============================================================
// Phase 7.2.4 — FullSummary builder
// Maps API HealthReportDetail (+ optional previous) into the
// mock-shape FullSummaryRecord that the FE page already renders.
// ============================================================

const CATEGORY_EMOJI: Record<string, string> = {
  heart: "🫀",
  sugar: "🍬",
  lipid: "🥑",
  liver: "🩺",
  kidney: "💧",
  lung: "🫁",
  general: "📋",
};

const VITAL_TEST_MAP: Array<{
  id: VitalSign["id"];
  label: string;
  emoji: string;
  candidates: string[];
  color: string;
}> = [
  {
    id: "pulse",
    label: "ชีพจร",
    emoji: "💗",
    candidates: ["HR", "PULSE"],
    color: "#D83A5A",
  },
  {
    id: "temp",
    label: "อุณหภูมิ",
    emoji: "🌡️",
    candidates: ["TEMP", "BT"],
    color: "#E89740",
  },
  {
    id: "resp",
    label: "การหายใจ",
    emoji: "🫁",
    candidates: ["RESP", "RR"],
    color: "#5BC78A",
  },
  {
    id: "spo2",
    label: "ออกซิเจน",
    emoji: "💧",
    candidates: ["SPO2", "O2SAT"],
    color: "#3B82F6",
  },
];

function findTest(
  report: HealthReportDetail,
  codes: string[],
): LabResultView | null {
  for (const c of report.categories) {
    for (const t of c.tests) {
      if (codes.includes(t.testCode)) return t;
    }
  }
  return null;
}

function parseJsonSafe(val: any): any {
  if (val === null || val === undefined) return null;
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (!trimmed) return null;
    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }
  return val;
}

function buildRealSummary(
  report: HealthReportDetail,
  prev: HealthReportDetail | null,
): MockFullSummary {
  // -------- vitals --------
  const vitals: VitalSign[] = [];
  const rawVitals = parseJsonSafe(report.vitals);

  if (Array.isArray(rawVitals)) {
    for (const item of rawVitals) {
      if (item && item.value !== undefined && item.value !== null) {
        vitals.push({
          id: (item.id ?? "pulse") as VitalSign["id"],
          label: item.label ?? item.id ?? "",
          emoji: item.emoji ?? "💗",
          value: item.value,
          unit: item.unit ?? "",
          color: item.color ?? "#D83A5A",
        });
      }
    }
  } else if (rawVitals && typeof rawVitals === "object") {
    const bp = rawVitals.bloodPressure ?? rawVitals.bp;
    if (bp && typeof bp === "object" && (bp.systolic != null || bp.diastolic != null)) {
      vitals.push({
        id: "bp" as VitalSign["id"],
        label: "ความดันโลหิต",
        emoji: "🩺",
        value: `${bp.systolic ?? "—"}/${bp.diastolic ?? "—"}`,
        unit: bp.unit ?? "mmHg",
        color: "#D83A5A",
      });
    }
    const hr = rawVitals.heartRate ?? rawVitals.pulse ?? rawVitals.pluse ?? rawVitals.hr ?? rawVitals.pr;
    const hrVal = typeof hr === "object" ? hr?.value : hr;
    if (hrVal != null) {
      vitals.push({
        id: "pulse",
        label: "ชีพจร",
        emoji: "💗",
        value: hrVal,
        unit: (typeof hr === "object" ? hr?.unit : null) ?? "bpm",
        color: "#D83A5A",
      });
    }
    const temp = rawVitals.temperature ?? rawVitals.temp ?? rawVitals.bt;
    const tempVal = typeof temp === "object" ? temp?.value : temp;
    if (tempVal != null) {
      vitals.push({
        id: "temp",
        label: "อุณหภูมิ",
        emoji: "🌡️",
        value: tempVal,
        unit: (typeof temp === "object" ? temp?.unit : null) ?? "°C",
        color: "#E89740",
      });
    }
    const rr = rawVitals.respiratoryRate ?? rawVitals.rr ?? rawVitals.resp;
    const rrVal = typeof rr === "object" ? rr?.value : rr;
    if (rrVal != null) {
      vitals.push({
        id: "resp",
        label: "การหายใจ",
        emoji: "🫁",
        value: rrVal,
        unit: (typeof rr === "object" ? rr?.unit : null) ?? "/min",
        color: "#5BC78A",
      });
    }
    const spo2 = rawVitals.oxygenSaturation ?? rawVitals.spo2 ?? rawVitals.spO2 ?? rawVitals.o2z ?? rawVitals.o2sat;
    const spo2Val = typeof spo2 === "object" ? spo2?.value : spo2;
    if (spo2Val != null) {
      vitals.push({
        id: "spo2",
        label: "ออกซิเจน",
        emoji: "💧",
        value: spo2Val,
        unit: (typeof spo2 === "object" ? spo2?.unit : null) ?? "%",
        color: "#3B82F6",
      });
    }
  }

  // Fallback to lab test rows if vitals array is still empty
  if (vitals.length === 0) {
    const bpSys = findTest(report, ["BP_SYS"]);
    const bpDia = findTest(report, ["BP_DIA"]);
    if (bpSys || bpDia) {
      vitals.push({
        id: "pulse" as VitalSign["id"],
        label: "ความดันโลหิต",
        emoji: "🩸",
        value:
          bpSys && bpDia
            ? `${bpSys.valueNumeric ?? "—"}/${bpDia.valueNumeric ?? "—"}`
            : (bpSys?.valueNumeric ?? bpDia?.valueNumeric ?? "—"),
        unit: bpSys?.unit ?? bpDia?.unit ?? "mmHg",
        color: "#D83A5A",
      });
    }
    for (const slot of VITAL_TEST_MAP) {
      if (slot.id === "pulse" && bpSys) continue;
      const t = findTest(report, slot.candidates);
      if (!t) continue;
      vitals.push({
        id: slot.id,
        label: slot.label,
        emoji: slot.emoji,
        value: t.valueNumeric ?? "—",
        unit: t.unit ?? "",
        color: slot.color,
      });
    }
  }

  // -------- categories (summary view per bucket) --------
  const categories: MockSummaryCategory[] = report.categories.map((c) => {
    const abnormal = c.tests.filter(
      (t) => t.status === "warn" || t.status === "bad",
    );
    let summary: string;
    if (abnormal.length === 0)
      summary = `ตรวจ ${c.tests.length} ค่า · ทั้งหมดอยู่ในเกณฑ์ปกติ`;
    else if (abnormal.length === c.tests.length)
      summary = `ทุกค่าอยู่นอกเกณฑ์ — ต้องดูแลทันที`;
    else summary = `${abnormal.length} จาก ${c.tests.length} ค่าต้องเฝ้าระวัง`;
    return {
      id: c.category as string,
      type: c.category as MockCategoryType,
      name: CATEGORY_INFO[c.category]?.name ?? (c.category as string),
      emoji: CATEGORY_EMOJI[c.category] ?? "📋",
      status: (c.status ?? "ok") as TestStatus,
      summary,
      abnormalTests: abnormal.slice(0, 3).map((t) => ({
        id: t.testCode.toLowerCase(),
        name: t.name,
        value: t.valueNumeric ?? 0,
        unit: t.unit ?? "",
        status: (t.status ?? "ok") as TestStatus,
      })),
    };
  });

  // -------- comparisons (latest vs previous) --------
  const comparisons: MockComparisonRow[] = [];
  if (prev) {
    const prevTests = new Map<string, LabResultView>();
    for (const c of prev.categories)
      for (const t of c.tests) prevTests.set(t.testCode, t);
    // Walk latest's tests and emit a comparison row for those with a
    // numeric counterpart in `prev`. Prioritise abnormal-changed tests
    // at the top so the table surfaces the most actionable rows first.
    const rows: MockComparisonRow[] = [];
    for (const c of report.categories) {
      for (const t of c.tests) {
        if (t.valueNumeric === null) continue;
        const old = prevTests.get(t.testCode);
        if (!old || old.valueNumeric === null) continue;
        if (old.valueNumeric === t.valueNumeric) continue;
        rows.push({
          label: t.name,
          oldValue: old.valueNumeric,
          newValue: t.valueNumeric,
          unit: t.unit ?? "",
          trend: deriveTrend(
            old.valueNumeric,
            t.valueNumeric,
            t.status ?? "ok",
          ),
        });
      }
    }
    // Sort: changes that moved to bad/warn first.
    rows.sort((a, b) => trendRank(b.trend) - trendRank(a.trend));
    comparisons.push(...rows.slice(0, 6));
  }

  // -------- score + delta --------
  const score = report.overallScore ?? 0;
  const prevScore = prev?.overallScore ?? null;
  const scoreDelta = prevScore !== null ? score - prevScore : 0;
  const scoreStatus =
    report.overallStatus === "ok"
      ? "ปกติทุกค่า"
      : report.overallStatus === "watch"
        ? "ดี · ต้องเฝ้าระวัง"
        : "ต้องดูแล · ผิดปกติบางค่า";

  // -------- final shape --------
  return {
    reportDate: formatThaiDate(report.reportDate),
    reportNumber: list.value.length, // best-effort: number = how many reports the customer has
    score,
    scoreStatus,
    scoreDelta,
    vitalsMeasuredAt:
      new Date(report.vitalsMeasuredAt ?? report.reportDate).toLocaleTimeString("th-TH", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }) + " น.",
    vitals,
    categories,
    comparisons,
    doctorSummary: report.doctorAdvice ?? "ไม่มีคำแนะนำเพิ่มเติม",
    doctor: {
      name: report.certifiedBy.usrid ?? "ระบบ",
      specialty: "",
      licenseNo: report.certifiedBy.hospId ?? "",
    },
  };
}

function deriveTrend(
  oldV: number,
  newV: number,
  status: string,
): ComparisonTrend {
  const up = newV > oldV;
  if (status === "bad") return up ? "up-bad" : "down-bad";
  if (status === "warn") return up ? "up-warn" : "down-warn";
  return up ? "up-ok" : "down-ok";
}

function trendRank(t: ComparisonTrend): number {
  return (
    {
      "up-bad": 6,
      "down-bad": 5,
      "up-warn": 4,
      "down-warn": 3,
      "down-ok": 2,
      "up-ok": 1,
      flat: 0,
      "first-time": 0,
    }[t] ?? 0
  );
}

function deriveRecommendations(t: LabResultView): string[] {
  if (t.status === "bad") {
    return [
      "พบเจ้าหน้าที่โรงพยาบาลเพื่อตรวจติดตามทันที",
      "ปรับพฤติกรรมการกิน — ลดอาหารหวาน มัน เค็ม",
      "ออกกำลังกายอย่างน้อย 30 นาที 3-5 วัน/สัปดาห์",
    ];
  }
  if (t.status === "warn") {
    return [
      "ตรวจซ้ำใน 3 เดือน",
      "ปรับพฤติกรรมการกิน — ลดอาหารหวาน มัน เค็ม",
      "ออกกำลังกายอย่างน้อย 30 นาที 3 วัน/สัปดาห์",
    ];
  }
  return [
    "รักษาวิถีชีวิตที่ดีต่อไป",
    "ตรวจติดตามประจำปี",
    "ดื่มน้ำให้เพียงพอ + พักผ่อนเพียงพอ",
  ];
}

// ============================================================
// Public API
// ============================================================

export function useHealthReport() {
  const categories = computed<ApiCategoryRecord[]>(
    () => latest.value?.categories ?? [],
  );

  /** API LabResultView for the given test code, regardless of category. */
  function getTest(testCode: string): LabResultView | null {
    for (const c of categories.value) {
      const t = c.tests.find((x) => x.testCode === testCode);
      if (t) return t;
    }
    return null;
  }

  /** API CategoryRecord for the given category name. */
  function getCategoryApi(category: string): ApiCategoryRecord | null {
    return findCategoryInLatest(category);
  }

  /**
   * Mock-shape category — auto-falls back to the static mock when no
   * real report exists, so the 4 health pages keep working for fresh
   * customers / unauthenticated viewers.
   */
  function getCategory(type: string): MockCategoryRecord | null {
    if (!latest.value) return null;
    const apiCat = findCategoryInLatest(type);
    if (!apiCat) return null;
    return apiToMockCategoryRecord(latest.value, apiCat);
  }

  /**
   * Mock-shape test detail. Tries the real report first by either
   * testCode (LDL, FBS) or by lowercase id (ldl, fbs). Falls back to
   * the mock when not found. History is fetched lazily — caller should
   * await `getTestDetailAsync()` when they need the trend chart.
   */
  function getTestDetail(idOrCode: string): MockTestDetail | null {
    if (!latest.value) return null;
    const code = idOrCode.toUpperCase();
    let test: LabResultView | null = null;
    let category: ApiCategoryRecord | null = null;
    for (const c of latest.value.categories) {
      const t = c.tests.find(
        (x) =>
          x.testCode === code ||
          x.testCode.toLowerCase() === idOrCode.toLowerCase(),
      );
      if (t) {
        test = t;
        category = c;
        break;
      }
    }
    if (!test || !category) return null;
    // Synchronous variant uses an empty history; pages that want the
    // trend should call getTestDetailAsync().
    return apiToMockTestDetail(latest.value, test, category, []);
  }

  /** Async variant — includes the real test history. */
  async function getTestDetailAsync(
    idOrCode: string,
  ): Promise<MockTestDetail | null> {
    const sync = getTestDetail(idOrCode);
    if (!sync || !latest.value) return sync;
    const code = idOrCode.toUpperCase();
    let test: LabResultView | null = null;
    let category: ApiCategoryRecord | null = null;
    for (const c of latest.value.categories) {
      const t = c.tests.find(
        (x) =>
          x.testCode === code ||
          x.testCode.toLowerCase() === idOrCode.toLowerCase(),
      );
      if (t) {
        test = t;
        category = c;
        break;
      }
    }
    if (!test || !category) return sync;
    const history = await loadHistory(test.testCode);
    return apiToMockTestDetail(latest.value, test, category, history);
  }

  /**
   * Phase 7.2.4 — full-data summary derived from `latest` (+ `previous`
   * for year-over-year deltas). Falls back to the static mock when no
   * real report exists (fresh customer / unauth viewer).
   *
   * For comparisons + scoreDelta to populate, the page must call
   * `loadFullSummary()` first — that fetches latest+previous together.
   */
  function getFullSummary(): MockFullSummary | null {
    if (!latest.value) return null;
    return buildRealSummary(latest.value, previous.value);
  }

  /** Re-export for pages that already used the mock helper. */
  function listCategoryTypes(): MockCategoryType[] {
    return ["heart", "sugar", "lung", "lipid", "liver", "kidney", "general"];
  }

  function reset(): void {
    list.value = [];
    latest.value = null;
    previous.value = null;
    error.value = null;
  }

  return {
    list: readonly(list),
    latest: readonly(latest),
    previous: readonly(previous),
    categories,
    listLoading: readonly(listLoading),
    latestLoading: readonly(latestLoading),
    error: readonly(error),
    refreshList,
    loadLatest,
    loadReport,
    loadHistory,
    loadFullSummary,
    reset,
    // Drop-in replacements for the mock composable's helpers — same
    // names so the 3 sub-pages only need to swap the import.
    getCategory,
    getTest: getTestDetail,
    getTestDetail,
    getTestDetailAsync,
    getFullSummary,
    listCategoryTypes,
    // Original API-shape helpers (kept for callers that want the raw
    // LabResultView instead of the mock-shape CategoryTest).
    getTestApi: getTest,
    getCategoryApi,
  };
}
